import { commonRunEditState } from "./common_run_edit_state";
import { commonNucleusInventoryHealthUnfulfilledDemandRecipe } from "./common_nucleus_customer_capabilities";
import type { InventoryHealthVariant } from "./inventory_health_cell_context";
import { formatLocaleDecimalMax2 } from "./locale_decimal_max_2";

export type InventoryHealthUnfulfilledDemandPeriodField = "week" | "month";

export type CommonInventoryHealthUnfulfilledDemandCellContext = Readonly<{
  version: "CommonInventoryHealthUnfulfilledDemandCellContext.v1";
  surfaceId: "inventory-health";
  variant: InventoryHealthVariant;
  receiptId: string;
  measure: "unfulfilled_demand";
  cellKind: "period_leaf";
  period: Readonly<{
    field: InventoryHealthUnfulfilledDemandPeriodField;
    grain: InventoryHealthUnfulfilledDemandPeriodField;
    startEpochMs: number;
    endExclusiveEpochMs: number;
  }>;
  dimensions: readonly Readonly<{ field: string; value: string }>[];
  selector: Readonly<{
    segmentation: string;
    measure: "unfulfilled demand";
    measureRank: number;
    columnKey: string;
  }>;
  filters: readonly [];
  postFilters: readonly [];
  display: Readonly<{
    rawValue: string;
    unit: "units";
    rule: "inventory_matrix_v1";
  }>;
  state: Readonly<{
    basis: "base";
    edits: "none" | "saved";
    comparison: false;
  }>;
}>;

export type CommonInventoryHealthUnfulfilledDemandCellContextV1 =
  CommonInventoryHealthUnfulfilledDemandCellContext;

export type InventoryHealthUnfulfilledDemandCellContextInput = Readonly<{
  tenantId: unknown;
  variant: unknown;
  receiptId: unknown;
  columnKey: unknown;
  periodField: unknown;
  periodStartEpochMs: unknown;
  segments: readonly unknown[] | undefined;
  row: Readonly<Record<string, unknown>>;
  rawValue: unknown;
  selectedMeasure: unknown;
  filters: readonly unknown[] | undefined;
  postFilters: readonly unknown[] | undefined;
  settingsNameAndVersion: unknown;
  comparison: unknown;
}>;

const RECEIPT_ID = /^rvr1_[a-f0-9]{64}$/;
const POSITIVE_INTEGER = /^[1-9][0-9]{0,15}$/;

const utf8ByteLength = (value: string): number => {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit < 0x80) bytes += 1;
    else if (codeUnit < 0x800) bytes += 2;
    else if (
      codeUnit >= 0xd800 &&
      codeUnit <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
};

const boundedText = (value: unknown, maxBytes: number): string | undefined =>
  typeof value === "string" &&
  value.length > 0 &&
  value === value.trim() &&
  utf8ByteLength(value) <= maxBytes &&
  !/\p{Cc}/u.test(value)
    ? value
    : undefined;

const isVariant = (value: unknown): value is InventoryHealthVariant =>
  value === "standard" ||
  value === "with_transfers" ||
  value === "for_hubs" ||
  value === "raw";

const isPeriodField = (
  value: unknown,
): value is InventoryHealthUnfulfilledDemandPeriodField =>
  value === "week" || value === "month";

const exactRecipe = (
  tenantId: unknown,
  variant: InventoryHealthVariant,
  periodField: InventoryHealthUnfulfilledDemandPeriodField,
): readonly string[] | undefined =>
  commonNucleusInventoryHealthUnfulfilledDemandRecipe(
    tenantId,
    variant,
    periodField,
  );

const periodEndExclusive = (
  field: InventoryHealthUnfulfilledDemandPeriodField,
  value: unknown,
): number | undefined => {
  if (!Number.isSafeInteger(value) || (value as number) <= 0) return undefined;
  const start = new Date(value as number);
  if (
    Number.isNaN(start.getTime()) ||
    start.getUTCHours() !== 0 ||
    start.getUTCMinutes() !== 0 ||
    start.getUTCSeconds() !== 0 ||
    start.getUTCMilliseconds() !== 0 ||
    (field === "week" && start.getUTCDay() !== 1) ||
    (field === "month" && start.getUTCDate() !== 1)
  ) {
    return undefined;
  }
  const end =
    field === "week"
      ? Date.UTC(
          start.getUTCFullYear(),
          start.getUTCMonth(),
          start.getUTCDate() + 7,
        )
      : Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1);
  return Number.isSafeInteger(end) && end > (value as number) ? end : undefined;
};

const canonicalPositiveRawValue = (value: unknown): string | undefined => {
  if (
    (typeof value !== "number" && typeof value !== "string") ||
    (typeof value === "string" &&
      (value.length === 0 || value !== value.trim() || value.length > 128))
  ) {
    return undefined;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return undefined;
  return String(numeric);
};

const canonicalMeasureRank = (value: unknown): number | undefined =>
  Number.isSafeInteger(value) &&
  (value as number) >= 1 &&
  (value as number) <= 5_000_000
    ? (value as number)
    : undefined;

const dimensionsFromInput = (
  segments: readonly unknown[] | undefined,
  row: Readonly<Record<string, unknown>>,
  expectedFields: readonly string[],
): readonly Readonly<{ field: string; value: string }>[] | undefined => {
  if (
    !Array.isArray(segments) ||
    segments.length !== expectedFields.length ||
    !segments.every((field, index) => field === expectedFields[index])
  ) {
    return undefined;
  }
  const dimensions: Array<Readonly<{ field: string; value: string }>> = [];
  for (const field of expectedFields) {
    const value = boundedText(row[field], 512);
    if (!value) return undefined;
    dimensions.push(Object.freeze({ field, value }));
  }
  return Object.freeze(dimensions);
};

export function createInventoryHealthUnfulfilledDemandCellContext(
  input: InventoryHealthUnfulfilledDemandCellContextInput,
): CommonInventoryHealthUnfulfilledDemandCellContext | undefined {
  if (
    !isVariant(input.variant) ||
    typeof input.receiptId !== "string" ||
    !RECEIPT_ID.test(input.receiptId) ||
    !isPeriodField(input.periodField) ||
    typeof input.columnKey !== "string" ||
    !POSITIVE_INTEGER.test(input.columnKey) ||
    !Number.isSafeInteger(Number(input.columnKey)) ||
    String(Number(input.columnKey)) !== input.columnKey ||
    Number(input.columnKey) !== input.periodStartEpochMs ||
    typeof input.selectedMeasure !== "string" ||
    input.selectedMeasure.trim().toLowerCase() !== "unfulfilled demand" ||
    !input.row ||
    typeof input.row !== "object" ||
    Array.isArray(input.row) ||
    input.row.type !== "group" ||
    typeof input.row.measure !== "string" ||
    input.row.measure.trim().toLowerCase() !== "unfulfilled demand" ||
    !Array.isArray(input.filters) ||
    input.filters.length !== 0 ||
    !Array.isArray(input.postFilters) ||
    input.postFilters.length !== 0 ||
    input.comparison !== false
  ) {
    return undefined;
  }

  const expectedFields = exactRecipe(
    input.tenantId,
    input.variant,
    input.periodField,
  );
  if (!expectedFields) return undefined;
  const endExclusiveEpochMs = periodEndExclusive(
    input.periodField,
    input.periodStartEpochMs,
  );
  const dimensions = dimensionsFromInput(
    input.segments,
    input.row,
    expectedFields,
  );
  const segmentation = boundedText(input.row.segmentation, 2_048);
  const measureRank = canonicalMeasureRank(input.row.measureRank);
  const rawValue = canonicalPositiveRawValue(input.rawValue);
  const edits = commonRunEditState(input.settingsNameAndVersion);
  if (
    !endExclusiveEpochMs ||
    !dimensions ||
    !segmentation ||
    segmentation !== dimensions.map(({ value }) => value).join(" | ") ||
    measureRank === undefined ||
    rawValue === undefined ||
    !edits
  ) {
    return undefined;
  }

  return Object.freeze({
    version: "CommonInventoryHealthUnfulfilledDemandCellContext.v1",
    surfaceId: "inventory-health",
    variant: input.variant,
    receiptId: input.receiptId,
    measure: "unfulfilled_demand",
    cellKind: "period_leaf",
    period: Object.freeze({
      field: input.periodField,
      grain: input.periodField,
      startEpochMs: input.periodStartEpochMs as number,
      endExclusiveEpochMs,
    }),
    dimensions,
    selector: Object.freeze({
      segmentation,
      measure: "unfulfilled demand",
      measureRank,
      columnKey: input.columnKey,
    }),
    filters: Object.freeze([]) as readonly [],
    postFilters: Object.freeze([]) as readonly [],
    display: Object.freeze({
      rawValue,
      unit: "units",
      rule: "inventory_matrix_v1",
    }),
    state: Object.freeze({ basis: "base", edits, comparison: false }),
  });
}

export function isSupportedInventoryHealthUnfulfilledDemandCellContext(
  context: CommonInventoryHealthUnfulfilledDemandCellContext,
  tenantId: unknown,
): boolean {
  if (
    context.version !==
      "CommonInventoryHealthUnfulfilledDemandCellContext.v1" ||
    context.surfaceId !== "inventory-health" ||
    !isVariant(context.variant) ||
    !RECEIPT_ID.test(context.receiptId) ||
    context.measure !== "unfulfilled_demand" ||
    context.cellKind !== "period_leaf" ||
    !isPeriodField(context.period.field) ||
    context.period.grain !== context.period.field
  ) {
    return false;
  }
  const expectedFields = exactRecipe(
    tenantId,
    context.variant,
    context.period.field,
  );
  return Boolean(
    expectedFields &&
      periodEndExclusive(context.period.field, context.period.startEpochMs) ===
        context.period.endExclusiveEpochMs &&
      context.dimensions.length === expectedFields.length &&
      context.dimensions.every(
        (dimension, index) =>
          dimension.field === expectedFields[index] &&
          Boolean(boundedText(dimension.value, 512)),
      ) &&
      context.selector.segmentation ===
        context.dimensions.map(({ value }) => value).join(" | ") &&
      Boolean(boundedText(context.selector.segmentation, 2_048)) &&
      context.selector.measure === "unfulfilled demand" &&
      canonicalMeasureRank(context.selector.measureRank) ===
        context.selector.measureRank &&
      context.selector.columnKey === String(context.period.startEpochMs) &&
      context.filters.length === 0 &&
      context.postFilters.length === 0 &&
      canonicalPositiveRawValue(context.display.rawValue) ===
        context.display.rawValue &&
      context.display.unit === "units" &&
      context.display.rule === "inventory_matrix_v1" &&
      context.state.basis === "base" &&
      (context.state.edits === "none" || context.state.edits === "saved") &&
      context.state.comparison === false,
  );
}

const friendlyDimension = (
  dimension: Readonly<{ field: string; value: string }>,
): string => {
  const field =
    dimension.field === "sku"
      ? "SKU"
      : dimension.field === "location"
        ? "Location"
        : dimension.field
            .replaceAll("_", " ")
            .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const value =
    dimension.value === "__NULL__" ? "not specified" : dimension.value;
  return `${field}: ${value}`;
};

export function inventoryHealthUnfulfilledDemandCellPrompt(
  context: CommonInventoryHealthUnfulfilledDemandCellContext,
): string {
  const dimensions = context.dimensions.map(friendlyDimension).join(" | ");
  const numericValue = Number(context.display.rawValue);
  const displayedValue =
    formatLocaleDecimalMax2(numericValue, "en-US") ?? context.display.rawValue;
  const unitLabel = numericValue === 1 ? "unit" : "units";
  const date = new Date(context.period.startEpochMs);
  const formattedPeriod =
    context.period.grain === "month"
      ? new Intl.DateTimeFormat("en-US", {
          month: "long",
          year: "numeric",
          timeZone: "UTC",
        }).format(date)
      : new Intl.DateTimeFormat("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
          timeZone: "UTC",
        }).format(date);
  const period =
    context.period.grain === "month"
      ? `Month: ${formattedPeriod}`
      : `Week of ${formattedPeriod}`;
  return `Explain this unfulfilled demand value: ${displayedValue} ${unitLabel} | ${dimensions} | ${period}. Reconcile the exact displayed total, show its exact demand contributors and their estimated unfulfilled-demand allocation, clearly label estimates, and state any evidence or lineage gaps.`;
}
